import { AI_LITERACY_PACK } from "@engine/subjects/ai_literacy/subjectPack.js";
import { getSubjectPack, defaultSubjectId } from "@engine/index.js";
import { normalizeGameMasterId } from "@engine/gameMaster/registry.js";
import { DEFAULT_CHARACTER_ID, DEFAULT_GM_ID } from "./character.js";
import type { CharacterPreferences } from "./character.js";
import type { Locale } from "@engine/generate/conceptCopy.js";

export { DEFAULT_CHARACTER_ID } from "./character.js";
export { DEFAULT_GM_ID } from "./character.js";

const APP = "notanyai:";

/** Prefix used pre-rebrand (D.0.1+ → "notanyai:"). One-off rename in `runAllMigrations`. */
const PRE_REBRAND_APP = "ai-prepper:";

const LEGACY = APP;
const M1 = `${APP}${AI_LITERACY_PACK.id}:`;

const LEGACY_TO_M1: ReadonlyArray<readonly [string, string]> = [
  [`${M1}player`, `${LEGACY}player`],
  [`${M1}questions`, `${LEGACY}questions`],
  [`${M1}attempts`, `${LEGACY}attempts`],
  [`${M1}browser-mastery`, `${LEGACY}browser-mastery`],
  [`${M1}browser-session`, `${LEGACY}browser-session`],
  [`${M1}browser-view`, `${LEGACY}browser-view`],
  [`${M1}locale`, `${LEGACY}locale`],
  [`${M1}rotation:backpack`, `${LEGACY}rotation:backpack`],
  [`${M1}rotation:spotlie`, `${LEGACY}rotation:spotlie`],
  [`${M1}rotation:redact`, `${LEGACY}rotation:redact`],
];

const M1_TO_M2: ReadonlyArray<readonly [string, string]> = (() => {
  const p = storagePrefixFn(DEFAULT_CHARACTER_ID, AI_LITERACY_PACK.id);
  const keys = [
    "player",
    "questions",
    "attempts",
    "browser-mastery",
    "browser-session",
    "browser-view",
    "locale",
    "rotation:backpack",
    "rotation:spotlie",
    "rotation:redact",
  ] as const;
  return keys.map((k) => [`${p}${k}`, `${M1}${k}`] as const);
})();

function storagePrefixFn(charId: string, subjectId: string): string {
  return `${APP}${charId}:${subjectId}:`;
}

/** Build `notanyai:<characterId>:<subjectId>:` (M.2). */
export function storagePrefix(characterId: string, subjectId: string): string {
  return storagePrefixFn(characterId, subjectId);
}

/** Global: which character profile is active. M.2: `guest` only. */
export const ACTIVE_CHARACTER_KEY = `${APP}active-character`;

let activeStoragePrefix: string = storagePrefixFn(DEFAULT_CHARACTER_ID, AI_LITERACY_PACK.id);

/** After migrations + `loadActiveCharacterId()`. */
export function setActiveStoragePrefix(p: string): void {
  activeStoragePrefix = p;
}

export function getActiveStoragePrefix(): string {
  return activeStoragePrefix;
}

export function loadActiveCharacterId(): string {
  if (typeof localStorage === "undefined") return DEFAULT_CHARACTER_ID;
  const s = localStorage.getItem(ACTIVE_CHARACTER_KEY);
  if (s === DEFAULT_CHARACTER_ID) return s;
  if (s && /^[a-z0-9_]+$/.test(s)) return s;
  return DEFAULT_CHARACTER_ID;
}

export function saveActiveCharacterId(id: string): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(ACTIVE_CHARACTER_KEY, id);
}

/** Global: which subject pack is active (M.4). */
export const ACTIVE_SUBJECT_KEY = `${APP}active-subject`;

export function loadActiveSubjectId(): string {
  if (typeof localStorage === "undefined") return defaultSubjectId();
  const s = localStorage.getItem(ACTIVE_SUBJECT_KEY);
  if (s && getSubjectPack(s)) return s;
  return defaultSubjectId();
}

export function saveActiveSubjectId(id: string): void {
  if (typeof localStorage === "undefined") return;
  if (!getSubjectPack(id)) return;
  localStorage.setItem(ACTIVE_SUBJECT_KEY, id);
}

/**
 * One-time copies (idempotent): rebrand `ai-prepper:*` → `notanyai:*`, then pre–M.1 → M.1,
 * then M.1 → M.2 `guest`+subject. Each step copies only when the destination is empty and
 * the source exists, so re-running is a no-op.
 */
export function runAllMigrations(): void {
  if (typeof localStorage === "undefined") return;
  // Rebrand: every `ai-prepper:*` key copied (then removed) under the new `notanyai:*` prefix.
  // Done first so subsequent migration steps see the keys under the new namespace.
  const rebrandKeys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(PRE_REBRAND_APP)) rebrandKeys.push(k);
  }
  for (const oldKey of rebrandKeys) {
    const newKey = APP + oldKey.slice(PRE_REBRAND_APP.length);
    if (localStorage.getItem(newKey) == null) {
      const v = localStorage.getItem(oldKey);
      if (v != null) localStorage.setItem(newKey, v);
    }
    localStorage.removeItem(oldKey);
  }
  for (const [newKey, oldKey] of LEGACY_TO_M1) {
    if (localStorage.getItem(newKey) == null && localStorage.getItem(oldKey) != null) {
      localStorage.setItem(newKey, localStorage.getItem(oldKey)!);
    }
  }
  for (const [newKey, oldKey] of M1_TO_M2) {
    if (localStorage.getItem(newKey) == null && localStorage.getItem(oldKey) != null) {
      localStorage.setItem(newKey, localStorage.getItem(oldKey)!);
    }
  }
  if (localStorage.getItem(ACTIVE_CHARACTER_KEY) == null) {
    localStorage.setItem(ACTIVE_CHARACTER_KEY, DEFAULT_CHARACTER_ID);
  }
  if (localStorage.getItem(ACTIVE_SUBJECT_KEY) == null) {
    localStorage.setItem(ACTIVE_SUBJECT_KEY, defaultSubjectId());
  }
  // Retired internal pack — reset any persisted selection.
  if (localStorage.getItem(ACTIVE_SUBJECT_KEY) === "agrobid") {
    localStorage.setItem(ACTIVE_SUBJECT_KEY, defaultSubjectId());
  }
}

export const CHARACTER_PREFS_KEY = "character-prefs";

export function defaultCharacterPreferences(
  subjectId: string,
  locale: Locale
): CharacterPreferences {
  return { locale, gmId: DEFAULT_GM_ID, subjectId };
}

export function readCharacterPreferencesJson(
  storagePrefix: string,
  fallbackLocale: Locale,
  subjectId: string
): CharacterPreferences {
  if (typeof localStorage === "undefined") {
    return defaultCharacterPreferences(subjectId, fallbackLocale);
  }
  const raw = localStorage.getItem(storagePrefix + CHARACTER_PREFS_KEY);
  if (raw) {
    try {
      const o = JSON.parse(raw) as Record<string, unknown>;
      const locale =
        o.locale === "en" || o.locale === "es" || o.locale === "el" ? o.locale : fallbackLocale;
      const gm =
        typeof o.gmId === "string" && o.gmId.length > 0
          ? normalizeGameMasterId(o.gmId)
          : DEFAULT_GM_ID;
      const sub =
        typeof o.subjectId === "string" && o.subjectId.length > 0 ? o.subjectId : subjectId;
      return { locale, gmId: gm, subjectId: sub };
    } catch {
      // fall through
    }
  }
  const lo = localStorage.getItem(storagePrefix + "locale");
  if (lo === "en" || lo === "es" || lo === "el") {
    return defaultCharacterPreferences(subjectId, lo);
  }
  return defaultCharacterPreferences(subjectId, fallbackLocale);
}

export function writeCharacterPreferencesJson(
  storagePrefix: string,
  prefs: CharacterPreferences
): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(storagePrefix + CHARACTER_PREFS_KEY, JSON.stringify(prefs));
  // Keep standalone locale in sync for older readers / migration debugging.
  localStorage.setItem(storagePrefix + "locale", prefs.locale);
}
